import React, { useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import Icon from "@/components/Icon"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { supabase } from "@/lib/supabase"

interface DocumentTypeOption {
  value: string
  label: string
}

interface UploadDocumentDialogProps {
  personId: string
  documentTypes: DocumentTypeOption[]
  existingDocumentTypes: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

const UploadDocumentDialog = ({
  personId,
  documentTypes,
  existingDocumentTypes,
  open,
  onOpenChange,
}: UploadDocumentDialogProps) => {
  const [selectedType, setSelectedType] = useState("")
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const hasExistingDocument = selectedType && existingDocumentTypes.includes(selectedType)

  const handleChooseFile = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !selectedType) return

    setIsUploading(true)
    try {
      const fileExtension = file.name.split(".").pop()
      const timestamp = Date.now()
      const path = `${personId}/${selectedType}-${timestamp}.${fileExtension}`

      const { error: uploadError } = await supabase.storage
        .from("application-documents")
        .upload(path, file, {
          cacheControl: "3600",
          upsert: true,
        })

      if (uploadError) throw uploadError

      const { error: insertError } = await supabase.from("documents").insert({
        person_id: personId,
        type: selectedType,
        file: path,
        status: "Verified",
      })

      if (insertError) throw insertError

      queryClient.invalidateQueries({ queryKey: ["person-documents", personId] })
      toast.success("Document uploaded successfully")
      handleClose()
    } catch (error) {
      console.error("Upload failed:", error)
      toast.error("Failed to upload document")
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  const handleClose = () => {
    setSelectedType("")
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(val) => (val ? onOpenChange(true) : handleClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload document</DialogTitle>
          <DialogDescription className="text-palette-jungleGreen900 pt-2">
            Select the type of document you want to upload
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger>
              <SelectValue placeholder="Document type" />
            </SelectTrigger>
            <SelectContent>
              {documentTypes.map((dt) => (
                <SelectItem key={dt.value} value={dt.value}>
                  {dt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasExistingDocument && (
            <p className="text-sm">
              <span className="font-semibold">Please note:</span> a file is already uploaded for
              this document type. Choosing a new one will replace the existing one.
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={handleClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={handleChooseFile}
              disabled={!selectedType || isUploading}
            >
              {isUploading ? (
                <>
                  <Icon name="arrowClockwise" className="h-4 w-4 mr-2 animate-spin text-white" />
                  Uploading...
                </>
              ) : (
                <>
                  <Icon name="file" className="h-4 w-4 mr-2 text-white" />
                  Choose file
                </>
              )}
            </Button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,.pdf"
            onChange={handleFileChange}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default UploadDocumentDialog
